import { useEffect, useRef, useState } from "react";
import SplatViewer from "./SplatViewer";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2, Move3d, RotateCcw } from "lucide-react";

const SAMPLE_URL = "https://sparkjs.dev/assets/splats/butterfly.spz";
const DEFAULT_T = { tx: 0, ty: 0, tz: 0, rx: 0, ry: 0, rz: 0, scale: 1 };
const FT = { splat: "SPLAT", ksplat: "KSPLAT", ply: "PLY", spz: "SPZ", sog: "SOG" };
const SLIDERS = [
  ["tx", "X", -10, 10, 0.05], ["ty", "Y", -10, 10, 0.05], ["tz", "Z", -10, 10, 0.05],
  ["rx", "Rot X", -180, 180, 1], ["ry", "Rot Y", -180, 180, 1], ["rz", "Rot Z", -180, 180, 1],
  ["scale", "Scale", 0.05, 5, 0.05],
];

export default function SplatMode({ markers = [] }) {
  const [source, setSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [showAlign, setShowAlign] = useState(true);
  const [t, setT] = useState(DEFAULT_T);
  const fileRef = useRef(null);
  const blobRef = useRef(null);

  useEffect(() => () => { if (blobRef.current) URL.revokeObjectURL(blobRef.current); }, []);

  const openLocal = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (blobRef.current) URL.revokeObjectURL(blobRef.current);
    const url = URL.createObjectURL(f);
    blobRef.current = url;
    const ext = f.name.split(".").pop().toLowerCase();
    setErr(null); setLoading(true); setT(DEFAULT_T);
    setSource({ url, name: f.name, fileType: FT[ext], local: true });
  };
  const loadSample = () => { setErr(null); setLoading(true); setT(DEFAULT_T); setSource({ url: SAMPLE_URL, name: "butterfly.spz (sample)", fileType: "SPZ", local: false }); };
  const closeModel = () => {
    if (blobRef.current) { URL.revokeObjectURL(blobRef.current); blobRef.current = null; }
    setSource(null); setT(DEFAULT_T); setErr(null); setLoading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const markersWithPos = markers.filter((m) => m.position && m.position.x != null);

  return (
    <div className="absolute inset-0 z-20 bg-[#0A0A0A]" data-testid="splat-mode">
      <input ref={fileRef} type="file" accept=".ply,.splat,.ksplat,.spz,.sog" className="hidden" onChange={openLocal} data-testid="local-splat-input" />
      {!source ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 gap-4">
          <Move3d className="w-12 h-12 text-muted-foreground" />
          <div>
            <p className="font-display text-lg font-semibold">Gaussian Splat viewer</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm">Open a Gaussian Splat from your computer (.ply, .splat, .ksplat, .spz, .sog). The file stays on your machine — it is never uploaded or stored.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => fileRef.current?.click()} className="gap-2 bg-[#C5A24A] hover:bg-[#A8863A] text-black" data-testid="open-local-splat"><Upload className="w-4 h-4" /> Open Local Splat</Button>
            <Button variant="outline" onClick={loadSample} className="border-white/15 bg-transparent hover:bg-white/5" data-testid="load-sample-splat">Load sample</Button>
          </div>
        </div>
      ) : (
        <>
          <SplatViewer url={source.url} fileType={source.fileType} markers={markersWithPos} transform={t}
            onLoaded={() => setLoading(false)} onError={(e) => { setLoading(false); setErr(String(e?.message || e || "Failed to load splat")); }} />

          <div className="absolute top-3 left-3 right-3 z-20 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {source.local && <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#C5A24A] text-black" data-testid="local-banner">TEMPORARY LOCAL MODEL — not uploaded</span>}
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-black/70 border border-white/10 text-white truncate max-w-[200px]">{source.name}</span>
              <span className="text-[11px] px-2.5 py-1 rounded-full bg-black/70 border border-white/10 text-muted-foreground" data-testid="marker-count">{markersWithPos.length} 3D markers</span>
            </div>
            <Button size="sm" variant="outline" onClick={closeModel} className="gap-1 border-white/15 bg-black/70 text-xs" data-testid="close-splat"><X className="w-3.5 h-3.5" /> Close</Button>
          </div>

          {loading && !err && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
              <div className="flex items-center gap-2 text-sm"><Loader2 className="w-5 h-5 animate-spin text-[#C5A24A]" /> Loading splat…</div>
            </div>
          )}
          {err && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 z-10 px-8 text-center">
              <div>
                <p className="text-[#FF9500] font-semibold">Could not load splat</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm break-words">{err}</p>
                <Button size="sm" onClick={closeModel} className="mt-3 bg-[#C5A24A] hover:bg-[#A8863A] text-black">Try another</Button>
              </div>
            </div>
          )}

          {source.local && !err && (showAlign ? (
            <div className="absolute bottom-3 left-3 z-20 w-64 bg-[#121212]/95 border border-white/10 rounded-lg p-3 backdrop-blur" data-testid="align-controls">
              <div className="flex items-center justify-between mb-2">
                <p className="label-caps text-[#C5A24A]">Align markers</p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setT(DEFAULT_T)} title="Reset" className="text-muted-foreground hover:text-white" data-testid="align-reset"><RotateCcw className="w-3.5 h-3.5" /></button>
                  <button onClick={() => setShowAlign(false)} title="Hide" className="text-muted-foreground hover:text-white"><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {SLIDERS.map(([k, label, min, max, step]) => (
                <div key={k} className="flex items-center gap-2 mb-1.5">
                  <span className="text-[10px] text-muted-foreground w-10 shrink-0">{label}</span>
                  <input type="range" min={min} max={max} step={step} value={t[k]} onChange={(e) => setT((p) => ({ ...p, [k]: parseFloat(e.target.value) }))} className="flex-1 accent-[#C5A24A]" data-testid={`align-${k}`} />
                  <span className="text-[10px] text-white w-10 text-right tabular-nums">{t[k]}</span>
                </div>
              ))}
              <p className="text-[10px] text-muted-foreground mt-1">Translate/rotate/scale the marker set to line it up with the splat. Values are temporary.</p>
            </div>
          ) : (
            <Button size="sm" onClick={() => setShowAlign(true)} className="absolute bottom-3 left-3 z-20 text-xs bg-[#121212] border border-white/10" data-testid="align-show">Align markers</Button>
          ))}
        </>
      )}
    </div>
  );
}
